import { useMemo, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import dayjs from 'dayjs'
import FilterListV2, { TFilterItem } from '@/components/content/FilterListV2'
import { Select } from '@/components/content/Select'
import { SearchableMultiSelect } from '@/components/content/SearchableMultiSelect'
import { DateRangePickerInput } from '@/components/content/DatePicker/DateRangePickerInput'
import { DateRangeValue } from '@/components/date/date.types'
import { Utils } from '@/utils/Utils'
import { DATE_FORMAT } from '@/utils/consts'
import { useGetBizestCategories, useGetPriceCodes } from '@/services/settlement/settlementPricing/useSettlementPricingQueries'
import { useGetCountries, useGetLogisticsCategories } from '@/services/commonCode/useCommonCodeQueries'
import { ORDER_TYPE_OPTIONS, STATUS_OPTIONS, UNIT_REGISTER_OPTIONS } from '../../consts'
import { CategoryPriceListSchemaType, getInitForm } from '../../schemas/categoryPriceListSchema'

interface Props {
  onSearch: (formValues: CategoryPriceListSchemaType) => void
  isLoading?: boolean
}

export function CategoryPriceForm({ onSearch, isLoading }: Props) {
  const { watch, setValue, handleSubmit, reset } = useFormContext<CategoryPriceListSchemaType>()
  const values = watch()
  const { data: codes } = useGetPriceCodes()
  const { data: countries } = useGetCountries() // 국가: moms BE 원천
  // 물류카테고리는 화면 전체(필터·컬럼·등록 모달)에서 logistics_category_code 로 통일 표기한다. (UI-BE 싱크 협의)
  const { data: logisticsCategories } = useGetLogisticsCategories() // 물류카테고리 코드(category####, moms BE) → '물류카테고리' 필터 옵션
  const { data: bizestCategories } = useGetBizestCategories() // 대분류(category_depth_1, mass BE) → 'bizest 카테고리' 필터 옵션

  const countryOptions = useMemo(() => Utils.createSelectOptions(countries ?? [], 'name', 'code'), [countries])
  const serviceOptions = useMemo(() => Utils.createSelectOptions(codes?.serviceTypes ?? [], 'name', 'code'), [codes?.serviceTypes])
  const logiOptions = useMemo(() => Utils.createSelectOptions(logisticsCategories ?? [], 'code', 'code'), [logisticsCategories])
  const bizestOptions = useMemo(() => Utils.createSelectOptions(bizestCategories ?? [], 'name', 'code'), [bizestCategories])

  const range: DateRangeValue = useMemo(() => [values.startDate, values.endDate], [values.startDate, values.endDate])
  const onRange = useCallback(
    (r: DateRangeValue) => {
      if (r && r[0] && r[1]) {
        setValue('startDate', dayjs(r[0]).format(DATE_FORMAT.date))
        setValue('endDate', dayjs(r[1]).format(DATE_FORMAT.date))
      } else {
        setValue('startDate', undefined)
        setValue('endDate', undefined)
      }
    },
    [setValue],
  )

  const filterList = useMemo<TFilterItem<CategoryPriceListSchemaType>[]>(
    () => [
      {
        field: 'country',
        label: '국가',
        required: true,
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 120 }}>
            <Select
              value={values.country}
              onValueChange={(v) => setValue('country', v)}
              options={countryOptions}
              placeholder="선택"
              floatingBlockLabel={isOnPanel ? undefined : '국가'}
              required
            />
          </div>
        ),
        fieldWidth: 120,
      },
      {
        // 대분류(category_depth_1) 다중선택 — 리스트 'bizest 카테고리' 컬럼(대>중>소)의 대분류 축
        field: 'bizestCategories',
        label: 'bizest 카테고리',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 200 }}>
            <SearchableMultiSelect
              value={values.bizestCategories ?? []}
              onValueChange={(v) => setValue('bizestCategories', v)}
              options={bizestOptions}
              placeholder="전체"
              floatingBlockLabel={isOnPanel ? undefined : 'bizest 카테고리'}
              width={isOnPanel ? undefined : 200}
            />
          </div>
        ),
        fieldWidth: 200,
      },
      {
        // 물류카테고리 코드(category####) 다중선택 — 리스트 '물류카테고리' 컬럼과 동일하게 코드로 표기
        field: 'logisticsCategoryCodes',
        label: '물류카테고리',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 260 }}>
            <SearchableMultiSelect
              value={values.logisticsCategoryCodes ?? []}
              onValueChange={(v) => setValue('logisticsCategoryCodes', v)}
              options={logiOptions}
              placeholder="전체"
              floatingBlockLabel={isOnPanel ? undefined : '물류카테고리'}
              width={isOnPanel ? undefined : 260}
            />
          </div>
        ),
        fieldWidth: 260,
      },
      {
        field: 'serviceType',
        label: '서비스 항목',
        required: true,
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 160 }}>
            <Select
              value={values.serviceType}
              onValueChange={(v) => setValue('serviceType', v)}
              options={serviceOptions}
              placeholder="선택"
              floatingBlockLabel={isOnPanel ? undefined : '서비스 항목'}
              required
            />
          </div>
        ),
        fieldWidth: 160,
      },
      {
        field: 'orderType',
        label: '주문유형',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 110 }}>
            <Select
              value={values.orderType ?? undefined}
              onValueChange={(v) => setValue('orderType', v)}
              onClear={() => setValue('orderType', null)}
              options={ORDER_TYPE_OPTIONS}
              placeholder="전체"
              floatingBlockLabel={isOnPanel ? undefined : '주문유형'}
            />
          </div>
        ),
        fieldWidth: 110,
      },
      {
        field: 'unitType',
        label: '단위',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 110 }}>
            <Select
              value={values.unitType ?? undefined}
              onValueChange={(v) => setValue('unitType', v)}
              onClear={() => setValue('unitType', null)}
              options={UNIT_REGISTER_OPTIONS}
              placeholder="전체"
              floatingBlockLabel={isOnPanel ? undefined : '단위'}
            />
          </div>
        ),
        fieldWidth: 110,
      },
      {
        field: 'status',
        label: '상태',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 110 }}>
            <Select
              value={values.status ?? undefined}
              onValueChange={(v) => setValue('status', v)}
              onClear={() => setValue('status', null)}
              options={STATUS_OPTIONS}
              placeholder="전체"
              floatingBlockLabel={isOnPanel ? undefined : '상태'}
            />
          </div>
        ),
        fieldWidth: 110,
      },
      {
        field: ['startDate', 'endDate'],
        label: '적용기간',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 260 }}>
            <DateRangePickerInput value={range} onChange={onRange} height={44} fixedCalendar={!isOnPanel} />
          </div>
        ),
        fieldWidth: 260,
      },
    ],
    [
      values.country,
      values.logisticsCategoryCodes,
      values.bizestCategories,
      values.serviceType,
      values.orderType,
      values.unitType,
      values.status,
      countryOptions,
      serviceOptions,
      logiOptions,
      bizestOptions,
      range,
      onRange,
      setValue,
    ],
  )

  return (
    <FilterListV2<CategoryPriceListSchemaType>
      list={filterList}
      onSearch={handleSubmit(onSearch)}
      onReset={() => reset(getInitForm())}
      isSubmitting={isLoading}
      initialValues={getInitForm()}
    />
  )
}
